/**
 * Lightweight client-side 10-year projection — mirrors backend
 * services/projection.py methodology so numbers shown in the Saved-Properties
 * Elite teaser match what an Elite user would see in AnalysisModal.
 *
 * Returns null if the input analysis is incomplete or the result would be
 * scary/wonky (negative initial cash, missing rent, negative total return).
 * The caller hides the UI in that case so we never show inflated figures.
 *
 * Default assumptions match backend project_ten_years() defaults.
 */
const ASSUMPTIONS = {
  rent_growth_pct: 3.0,
  expense_growth_pct: 2.5,
  appreciation_pct: 3.5,
  selling_cost_pct: 6.0,
  hold_years: 10,
};

function remainingBalance(loan, annualRate, termYears, monthsElapsed) {
  if (loan <= 0 || monthsElapsed <= 0 || termYears <= 0) return loan;
  const r = annualRate / 100 / 12;
  const n = termYears * 12;
  if (monthsElapsed >= n) return 0;
  if (r === 0) return Math.max(0, loan - (loan / n) * monthsElapsed);
  const totFactor = Math.pow(1 + r, n);
  return loan * (totFactor - Math.pow(1 + r, monthsElapsed)) / (totFactor - 1);
}

export function computeProjectionTeaser(analysis) {
  if (!analysis) return null;

  const purchasePrice = Number(analysis.purchase_price) || 0;
  const initialCash = Number(analysis.total_capital_needed) || 0;
  const monthlyRent = Number(analysis.estimated_monthly_rent) || 0;
  const annualExpensesY1 = Number(analysis.total_yearly_expenses) || 0;
  const annualDebtService = (Number(analysis.monthly_mortgage) || 0) * 12;
  const loanAmount = Number(analysis.loan_amount) || 0;
  const interestRate = Number(analysis.interest_rate) || 0;
  const termYears = Number(analysis.loan_term_years) || 30;
  const vacancyAmt = Number(analysis.vacancy_amount) || 0;
  const vacancyRate = monthlyRent > 0 ? (vacancyAmt / monthlyRent) : 0.05;

  // Guard against incomplete data — hide teaser instead of showing garbage.
  if (purchasePrice <= 0 || monthlyRent <= 0 || initialCash <= 0) return null;

  const rentG = ASSUMPTIONS.rent_growth_pct / 100;
  const expG = ASSUMPTIONS.expense_growth_pct / 100;
  const appG = ASSUMPTIONS.appreciation_pct / 100;
  const hold = ASSUMPTIONS.hold_years;

  let totalCashFlow = 0;
  for (let y = 1; y <= hold; y++) {
    const grossRentY = monthlyRent * 12 * Math.pow(1 + rentG, y - 1);
    const vacancyLossY = grossRentY * vacancyRate;
    const expensesY = annualExpensesY1 * Math.pow(1 + expG, y - 1);
    const noiY = grossRentY - vacancyLossY - expensesY;
    const cashFlowY = noiY - annualDebtService;
    totalCashFlow += cashFlowY;
  }

  const salePrice = purchasePrice * Math.pow(1 + appG, hold);
  const sellingCosts = salePrice * (ASSUMPTIONS.selling_cost_pct / 100);
  const loanPayoff = remainingBalance(loanAmount, interestRate, termYears, hold * 12);
  const netSaleProceeds = salePrice - sellingCosts - loanPayoff;

  const totalReturn = totalCashFlow + netSaleProceeds - initialCash;
  const equityMultiple = initialCash > 0
    ? (totalCashFlow + netSaleProceeds) / initialCash
    : null;

  // Hide if the deal is so bad the projection is scary/negative — the upsell
  // would feel insulting on a money-losing property.
  if (totalReturn <= 0 || !equityMultiple || equityMultiple < 1) return null;

  // Approximate IRR via secant-method bracket. Skip if cash flows aren't healthy.
  // For simplicity in the teaser, derive an annualized return from equityMultiple
  // (close enough for blurred display, faster than full Newton-Raphson):
  //   IRR ≈ equityMultiple^(1/hold) - 1   (geometric annualized return)
  const irrApprox = Math.pow(equityMultiple, 1 / hold) - 1;
  const irrPct = irrApprox * 100;

  return {
    total_return: Math.round(totalReturn),
    irr_pct: Number(irrPct.toFixed(1)),
    equity_multiple: Number(equityMultiple.toFixed(2)),
    estimated_sale_price: Math.round(salePrice),
    hold_years: hold,
    assumptions: ASSUMPTIONS,
  };
}
